import React from 'react';
import { useForm } from 'react-hook-form';

import { updateUserConfig } from '../../../api/account';
import { AuthContextType } from '../../../features/auth/AuthContext';

interface ItemReprocessWaitTimeFormProps {
    auth: AuthContextType;
    value: number;
    minimum: number;
    onSaved: (value: number) => void;
}

interface ItemReprocessWaitTimeFormValues {
    item_reprocess_wait_minutes: number;
}

export const buildItemReprocessWaitTimePayload = (
    data: ItemReprocessWaitTimeFormValues,
) => ({
    item_reprocess_wait_minutes: data.item_reprocess_wait_minutes,
});

const ItemReprocessWaitTimeForm: React.FC<ItemReprocessWaitTimeFormProps> = ({
    auth,
    value,
    minimum,
    onSaved,
}) => {
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<ItemReprocessWaitTimeFormValues>({
        defaultValues: { item_reprocess_wait_minutes: value },
    });

    React.useEffect(() => {
        reset({ item_reprocess_wait_minutes: value });
    }, [reset, value]);

    const onSubmit = async (data: ItemReprocessWaitTimeFormValues) => {
        setErrorMessage(null);
        setSuccessMessage(null);
        setProcessMessage('저장 중입니다...');

        await updateUserConfig({
            auth,
            payload: buildItemReprocessWaitTimePayload(data),
            setErrorMessage,
            setProcessMessage,
            setSuccessMessage,
            setResult: () => onSaved(data.item_reprocess_wait_minutes),
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="mb-4">
            <div className="mb-3">
                <label htmlFor="item-reprocess-wait-minutes" className="form-label">
                    상품 재작업 대기시간
                </label>
                <div className="input-group" style={{ width: '200px' }}>
                    <input
                        id="item-reprocess-wait-minutes"
                        type="number"
                        className={`form-control ${errors.item_reprocess_wait_minutes ? 'is-invalid' : ''}`}
                        defaultValue={value}
                        min={minimum}
                        max={30}
                        {...register('item_reprocess_wait_minutes', {
                            valueAsNumber: true,
                            required: '대기시간을 입력해주세요.',
                            min: { value: minimum, message: `대기시간은 ${minimum}분 이상이어야 합니다.` },
                            max: { value: 30, message: '대기시간은 30분 이하여야 합니다.' },
                            validate: (inputValue) => Number.isInteger(inputValue) || '분 단위 정수로 입력해주세요.',
                        })}
                    />
                    <span className="input-group-text">분</span>
                </div>
                {errors.item_reprocess_wait_minutes?.message && (
                    <div className="invalid-feedback d-block">
                        {String(errors.item_reprocess_wait_minutes.message)}
                    </div>
                )}
            </div>
            {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
            {processMessage && <div className="alert alert-info" role="alert">{processMessage}</div>}
            {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}
            <button type="submit" className="btn btn-primary">저장</button>
        </form>
    );
};

export default ItemReprocessWaitTimeForm;
